import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
const db = getFirestore(); // Initialize Firestore

/**
 * Fetch emails of users based on matching requests.
 * @param {string} type - "Buyer", "Seller", or "Both".
 * @param {string} grade - The product grade to match.
 * @param {string} form - The product form to match.
 * @returns {Promise<Array>} - Array of user emails.
 */
async function getInterestedUsers({ type, grade, form }) {
  const emails = [];

  try {
    // Step 1: Fetch matching requests
    const requests = await fetchMatchingRequests(type, grade, form);

    // Step 2: Extract unique user IDs from the requests
    const userIds = [...new Set(requests.map((request) => request.user_id))];

    // Step 3: Fetch user emails from the "users" collection
    for (const userId of userIds) {
      const userDocRef = doc(db, "users", userId); // Reference to the user document
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        if (userData.email) {
          emails.push(userData.email); // Add the email to the array
        }
      } else {
        console.warn(`User with ID ${userId} does not exist.`);
      }
    }

    return emails; // Return the array of emails
  } catch (error) {
    console.error("Error fetching user emails: ", error);
    throw error;
  }
}

/**
 * Fetch matching requests from Firestore based on type, product grade, and product form.
 * @param {string} type - "Buyer", "Seller", or "Both".
 * @param {string} grade - The product grade to match.
 * @param {string} form - The product form to match.
 * @returns {Promise<Array>} - Array of matching documents.
 */
async function fetchMatchingRequests(type, grade, form) {
  const requests = [];

  try {
    // Define the collections to query based on the type
    const collectionsToQuery = [];
    if (type === "Buyer" || type === "Both") {
      collectionsToQuery.push("buyer_requests");
    }
    if (type === "Seller" || type === "Both") {
      collectionsToQuery.push("seller_requests");
    }

    // Fetch documents from each collection
    for (const collectionName of collectionsToQuery) {
      const q = query(
        collection(db, collectionName),
        where("product.grade", "==", grade),
        where("product.form", "==", form)
      );

      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        requests.push({ id: doc.id, ...doc.data() });
      });
    }

    return requests; // Return the matching documents
  } catch (error) {
    console.error("Error fetching documents: ", error);
    throw error;
  }
}

export default getInterestedUsers;
